import { isPast } from '@nucleus/lib-datetime';
import { AuthorWeb } from '@nucleus/types/web/posts/author';
import { PostWeb } from '@nucleus/types/web/posts/post';
import { JsonLd } from '@nucleus/web-theme-elements';
import React from 'react';
import { BlogPosting, Person } from 'schema-dts';
import { useAllValidAuthors } from '../hooks/useAuthor';
import { useAuthorPath } from '../hooks/useAuthorPath';
import { usePostHubPath } from '../hooks/usePostHubPath';

interface PostStructuredDataProps {
  post?: PostWeb;
}

export const PostStructuredData = ({ post }: PostStructuredDataProps): JSX.Element | null => {
  if (post === undefined) {
    return null;
  }

  const authors = useAllValidAuthors(post.engineId, post.authors ?? []);
  const basePath = usePostHubPath(post.engineId);
  const toAuthorPath = useAuthorPath(basePath);

  const datePublished = post.publishedAt ?? post.date;

  return (
    // https://schema.org/BlogPosting
    // https://developers.google.com/search/docs/appearance/structured-data/article
    <JsonLd<BlogPosting>
      data={{
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        image: buildImage(post),
        datePublished: datePublished,
        dateModified: datePublished,
        author: buildAuthors(authors, toAuthorPath),
      }}
    />
  );
};

const buildAuthors = (
  authors: Array<AuthorWeb>,
  toAuthorPath: (author: AuthorWeb) => string
): Array<Person> | undefined => {
  if (authors.length === 0) {
    return undefined;
  }

  return authors.map((author) => ({
    '@type': 'Person' as const,
    name: author.displayName ?? '',
    ...(isPast(author.archivedAt) ? {} : { url: toAuthorPath(author) }),
  }));
};

const buildImage = (post: PostWeb): string | undefined => {
  return post.media?.main?.image?.src;
};
